<template>
	<Dialog v-model:visible="visible" modal :header="$t('dialogs.user_account_dialog.edit_profile')" :closable="!mustSetupTwoFactor" :closeOnEscape="!mustSetupTwoFactor">

		<template #header>
			<div class="inline-flex align-items-center justify-content-center gap-2">
				<IconUserCircle class="h-5 w-5 text-gray-400 mr-2"/>
				<span class="text-lg font-semibold leading-6 text-gray-900">{{ $t('dialogs.user_account_dialog.account_settings') }}</span>
			</div>
		</template>

		<TabView v-model:activeIndex="activeTabIndex">
			<TabPanel header="Profile">
				<form @submit.prevent class="flex flex-col gap-8 -mx-6">
					<FormGroup :label="$t('dialogs.user_account_dialog.name')" :error-text="form.errors?.name">
						<InputText
								ref="name"
								v-model="form.name"
								placeholder="e.g. Joe Bloggs"
								@change="form.validate('name')"
						/>
					</FormGroup>
					<FormGroup :label="$t('dialogs.user_account_dialog.email')" :error-text="form.errors?.email">
						<InputText
								ref="email"
								v-model="form.email"
								data-cy="email-input"
								placeholder="e.g. joe.bloggs@example.com"
								@change="form.validate('email')"
						/>
					</FormGroup>
					<FormGroup v-if="emailChanged" :label="$t('dialogs.user_account_dialog.current_password')" :error-text="form.errors?.current_password">
						<Password
								ref="current_password_profile"
								v-model="form.current_password"
								data-cy="current-password-input"
								placeholder="********"
								@blur="form.validate('current_password')"
								autocomplete="current-password"
								:feedback="false"
								toggleMask
								class="w-full"
								inputClass="w-full"
						/>
						<small class="text-gray-600">
							For security, your current password is required to change your email address.
						</small>
					</FormGroup>
				</form>
			</TabPanel>
			<TabPanel header="Password">
				<form @submit.prevent class="flex flex-col gap-8 -mx-6">
					<FormGroup :label="$t('dialogs.user_account_dialog.current_password')" :error-text="form.errors?.current_password">
						<Password
								ref="current_password"
								v-model="form.current_password"
								placeholder="********"
								@blur="form.validate('current_password')"
								autocomplete="current-password"
								:feedback="false"
								toggleMask
								inputClass="w-full"
						/>
					</FormGroup>
					<FormGroup :label="$t('dialogs.user_account_dialog.password')" :error-text="form.errors?.password">
						<Password
								ref="password"
								v-model="form.password"
								placeholder="********"
								@blur="form.validate('password')"
								autocomplete="new-password"
								toggleMask
								inputClass="w-full"
						/>
					</FormGroup>
					<FormGroup :label="$t('dialogs.user_account_dialog.password_confirm')" :error-text="form.errors?.password_confirmation">
						<Password
								ref="password_confirmation"
								v-model="form.password_confirmation"
								placeholder="********"
								@blur="form.validate('password_confirmation')"
								autocomplete="new-password"
								:feedback="false"
								toggleMask
								inputClass="w-full"
						/>
					</FormGroup>
				</form>
			</TabPanel>
			<TabPanel v-if="authStore.user.can_use_two_factor" header="Two-Factor Authentication">
				<div class="-mx-6">
					<!-- Step 1: Introduction -->
					<div v-if="twoFaStep === 1">
						<!-- Required Warning -->
						<div v-if="mustSetupTwoFactor" class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 text-center">
							<strong>Your company requires Two-Factor Authentication.</strong><br />
							You must set up 2FA before you can continue using SUMS.
						</div>

						<div class="flex items-center justify-center my-6">
							<IconLockCheck v-if="authStore.user.two_factor_enabled" class="w-24 h-24 text-green-600" />
							<IconLockOff v-else class="w-24 h-24 text-gray-300" />
						</div>
						<p class="text-center mb-4">
							<strong>{{ authStore.user.two_factor_enabled ? 'Two-Factor Authentication is Enabled' : 'Two-Factor Authentication is Not Enabled' }}</strong>
						</p>
						<p class="mb-4">
							Two-Factor authentication is a security feature that helps protect your account from unauthorised access.
						</p>
						<p class="mb-4">
							If enabled, you will be asked to enter a unique code from your authenticator app in addition to your password every time you log in.
						</p>
						<p class="mb-4"><strong>It is highly recommended that you add it to your account.</strong></p>
						<div class="bg-gray-100 p-4 rounded">
							<strong>Please note:</strong><br />
							You will need to install an authenticator app on your phone or tablet to use this feature, two recommended apps are linked below:<br /><br />
							<strong>Google Authenticator</strong> - <a class="underline text-primary-600" target="_blank" href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2&hl=en_GB&pli=1">Android</a> / <a class="underline text-primary-600" href="https://apps.apple.com/us/app/google-authenticator/id388497605" target="_blank">iOS</a><br /><br />
							<strong>Microsoft Authenticator</strong> - <a class="underline text-primary-600" target="_blank" href="https://play.google.com/store/apps/details?id=com.azure.authenticator&hl=en_GB">Android</a> / <a class="underline text-primary-600" href="https://apps.apple.com/us/app/microsoft-authenticator/id983156458" target="_blank">iOS</a>
						</div>
					</div>

					<!-- Step 7: Verify existing 2FA before showing QR -->
					<div v-if="twoFaStep === 7">
						<p class="mb-4">For security, please enter your current 2FA code to view the QR code:</p>
						<div class="flex justify-center gap-2 my-8">
							<input
								v-for="(box, index) in 6"
								:key="index"
								v-model="verifyTwoFaCode[index]"
								type="text"
								maxlength="1"
								class="w-12 h-12 text-center text-xl border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
								@input="handleVerifyTwoFaInput($event, index)"
								@paste="handleVerifyTwoFaPaste($event)"
								@focus="verifyTwoFaCodeErrors = null"
								ref="verifyTwoFaInputBoxes"
							/>
						</div>
						<div class="text-red-500 text-center" v-if="verifyTwoFaCodeErrors">{{ verifyTwoFaCodeErrors }}</div>
					</div>

					<!-- Step 2: QR Code -->
					<div v-if="twoFaStep === 2">
						<p class="mb-4">Open your Authenticator app and scan the QR code below:</p>
						<div class="p-6 text-center" v-if="qrCodeLoading">
							<ProgressSpinner />
						</div>
						<div class="p-6 text-center" v-else>
							<img :src="qrCodeUrl" alt="Two-Factor Authentication QR Code" class="mx-auto" />
						</div>
					</div>

					<!-- Step 3: Enter Code -->
					<div v-if="twoFaStep === 3">
						<p class="mb-4">Enter the code generated from the authenticator app on your new device below:</p>
						<div class="flex justify-center gap-2 my-8">
							<input
								v-for="(box, index) in 6"
								:key="index"
								v-model="twoFaCode[index]"
								type="text"
								maxlength="1"
								class="w-12 h-12 text-center text-xl border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
								@input="handleTwoFaInput($event, index)"
								@paste="handleTwoFaPaste($event)"
								@focus="twoFaCodeErrors = null"
								ref="twoFaInputBoxes"
							/>
						</div>
						<div class="text-red-500 text-center" v-if="twoFaCodeErrors">{{ twoFaCodeErrors }}</div>
					</div>

					<!-- Step 4: Success -->
					<div v-if="twoFaStep === 4">
						<div class="flex items-center justify-center my-6">
							<IconLockCheck class="w-24 h-24 text-green-600" />
						</div>
						<p class="mb-4"><strong>Thank you for enabling Two-Factor Authentication (2FA).</strong></p>
						<p class="mb-4">Please make a backup of these recovery codes in order to regain access to your account in the event you lose access to your authenticator app:</p>
						<div class="space-y-1">
							<div v-for="code in recoveryCodes" :key="code" class="p-2 bg-gray-200 rounded font-mono text-sm">
								{{ code }}
							</div>
						</div>
					</div>

					<!-- Step 5: Disable Confirmation -->
					<div v-if="twoFaStep === 5">
						<div class="flex items-center justify-center my-6">
							<IconLockOff class="w-24 h-24 text-gray-300" />
						</div>
						<p class="text-center mb-4">Are you sure you want to disable Two-Factor Authentication for this account?</p>
					</div>

					<!-- Step 6: Disabled Success -->
					<div v-if="twoFaStep === 6">
						<div class="flex items-center justify-center my-6">
							<IconLockOff class="w-24 h-24 text-red-600" />
						</div>
						<p class="text-center mb-4">Two-Factor Authentication has been disabled for this account.</p>
					</div>
				</div>
			</TabPanel>
		</TabView>

		<template #footer>
			<!-- 2FA Tab Step 1 footer -->
			<div v-if="activeTabIndex === 2 && twoFaStep === 1" class="flex justify-between w-full">
				<div>
					<Button v-if="authStore.user.two_factor_enabled" label="Disable 2FA" severity="danger" @click="twoFaStep = 5" />
				</div>
				<div>
					<Button :label="authStore.user.two_factor_enabled ? 'Show 2FA' : 'Setup 2FA'" @click="twoFaNextStep()" />
				</div>
			</div>
			<!-- 2FA Tab Step 2 footer -->
			<div v-else-if="activeTabIndex === 2 && twoFaStep === 2" class="flex justify-end w-full gap-2">
				<Button label="Back" outlined severity="secondary" @click="twoFaPrevStep()" />
				<Button label="I have scanned the QR code" @click="twoFaNextStep()" />
			</div>
			<!-- 2FA Tab Step 3 footer -->
			<div v-else-if="activeTabIndex === 2 && twoFaStep === 3" class="flex justify-end w-full gap-2">
				<Button label="Back" outlined severity="secondary" @click="twoFaPrevStep()" />
				<Button label="Verify Code" @click="validateTwoFactorCode()" />
			</div>
			<!-- 2FA Tab Step 4 footer -->
			<div v-else-if="activeTabIndex === 2 && twoFaStep === 4" class="flex justify-end w-full gap-2">
				<Button label="Download Recovery Codes" outlined severity="secondary" @click="downloadRecoveryCodes()" />
				<Button label="Done" @click="completeTwoFactorSetup()" />
			</div>
			<!-- 2FA Tab Step 5 footer -->
			<div v-else-if="activeTabIndex === 2 && twoFaStep === 5" class="flex justify-end w-full gap-2">
				<Button label="Cancel" outlined severity="secondary" @click="twoFaStep = 1" />
				<Button label="Yes, disable 2FA" severity="danger" @click="disableTwoFactorAuth()" />
			</div>
			<!-- 2FA Tab Step 6 footer -->
			<div v-else-if="activeTabIndex === 2 && twoFaStep === 6" class="flex justify-end w-full">
				<Button label="Done" @click="twoFaStep = 1" />
			</div>
			<!-- 2FA Tab Step 7 footer (verify existing 2FA) -->
			<div v-else-if="activeTabIndex === 2 && twoFaStep === 7" class="flex justify-end w-full gap-2">
				<Button label="Cancel" outlined severity="secondary" @click="twoFaStep = 1" />
				<Button label="Verify" @click="verifyExistingTwoFactor()" />
			</div>
			<!-- Default footer for other tabs -->
			<template v-else-if="activeTabIndex !== 2">
				<Button :label="$t('common.cancel')" text severity="secondary" data-cy="cancel-button" @click="cancel"/>
				<Button :label="$t('common.save')" outlined severity="secondary" data-cy="save-button" @click="confirm" autofocus />
			</template>
		</template>

	</Dialog>
</template>

<script>
import { useUsersStore } from "@/stores/users.js";
import { useAuthStore } from "@/stores/auth.js";
import { mapStores } from "pinia";
import { IconUserCircle, IconLockCheck, IconLockOff } from '@tabler/icons-vue';
import FormGroup from "@/Components/FormGroup.vue";

export default {
	name: "UserAccountDialog",
	emits: ['on-confirm', 'on-cancel'],
	components: {
		FormGroup,
		IconUserCircle,
		IconLockCheck,
		IconLockOff
	},
	data() {
		return {
			visible: false,
			form: null,
			originalEmail: '',
			activeTabIndex: 0,
			// 2FA data
			twoFaStep: 1,
			twoFaCode: [],
			twoFaCodeErrors: null,
			verifyTwoFaCode: [],
			verifyTwoFaCodeErrors: null,
			recoveryCodes: [],
			qrCodeUrl: null,
			qrCodeLoading: false
		}
	},
	computed: {
		...mapStores(useUsersStore),
		...mapStores(useAuthStore),
		emailChanged() {
			return this.form && this.form.email && this.form.email !== this.originalEmail;
		},
		mustSetupTwoFactor() {
			return this.authStore.user?.must_setup_two_factor;
		}
	},
	watch: {
		activeTabIndex(newIndex, oldIndex) {
			// Prevent switching away from 2FA tab if setup is required
			if (this.mustSetupTwoFactor && oldIndex === 2 && newIndex !== 2) {
				this.$nextTick(() => {
					this.activeTabIndex = 2;
				});
				return;
			}
			// Reset 2FA step when navigating away from the Two-Factor Auth tab
			if (oldIndex === 2 && newIndex !== 2) {
				this.twoFaStep = 1;
				this.twoFaCode = [];
				this.twoFaCodeErrors = null;
			}
		}
	},
	methods: {
		async open() {
			this.form = null;
			await this.authStore.fetchUser();
			this.form = await this.$auth.editSelf();
			this.originalEmail = this.form.email;
			this.activeTabIndex = 0;
			this.twoFaStep = 1;
			this.twoFaCode = [];
			this.twoFaCodeErrors = null;
			this.visible = true;
		},
		close() {
			this.visible = false;
		},
		async confirm() {
			await this.form.submit();
			window.location.reload();

			this.$emit('on-confirm');
			this.close();
		},
		cancel() {
			this.$emit('on-cancel');
			this.close();
		},
		// 2FA Methods
		twoFaPrevStep() {
			this.twoFaStep--;
			if (this.twoFaStep === 2) {
				this.startTwoFactorAuth();
			}
		},
		twoFaNextStep() {
			// If 2FA is already enabled and we're at step 1, go to verification step first
			if (this.twoFaStep === 1 && this.authStore.user.two_factor_enabled) {
				this.twoFaStep = 7;
				this.verifyTwoFaCode = [];
				this.verifyTwoFaCodeErrors = null;
				this.$nextTick(() => {
					if (this.$refs.verifyTwoFaInputBoxes && this.$refs.verifyTwoFaInputBoxes[0]) {
						this.$refs.verifyTwoFaInputBoxes[0].focus();
					}
				});
				return;
			}

			this.twoFaStep++;
			if (this.twoFaStep === 2) {
				this.startTwoFactorAuth();
			}
			if (this.twoFaStep === 3) {
				this.twoFaCode = [];
				this.twoFaCodeErrors = null;
				this.$nextTick(() => {
					if (this.$refs.twoFaInputBoxes && this.$refs.twoFaInputBoxes[0]) {
						this.$refs.twoFaInputBoxes[0].focus();
					}
				});
			}
			if (this.twoFaStep === 4) {
				this.fetchRecoveryCodes();
			}
		},
		async startTwoFactorAuth() {
			this.qrCodeLoading = true;
			try {
				await axios.post("user/two-factor-authentication");
				const response = await axios.get("user/two-factor-qr-code", {
					responseType: "blob",
				});
				this.qrCodeUrl = URL.createObjectURL(response.data);
			} catch (error) {
				console.error("Error enabling 2FA or fetching QR code", error);
			} finally {
				this.qrCodeLoading = false;
			}
		},
		async validateTwoFactorCode() {
			try {
				const code = this.twoFaCode.join('');
				const response = await axios.post("user/verify-two-factor-code", {
					code: code,
					confirm: true,
				});
				if (response.status === 200) {
					this.twoFaNextStep();
					this.authStore.user.two_factor_enabled = true;
				}
			} catch (error) {
				if (error.response?.data?.message) {
					this.twoFaCodeErrors = error.response.data.message;
				} else {
					this.twoFaCodeErrors = "Failed to verify code";
				}
				console.error("Error verifying 2FA code", error);
			}
		},
		async fetchRecoveryCodes() {
			try {
				const response = await axios.get("user/two-factor-recovery-codes");
				this.recoveryCodes = response.data;
			} catch (error) {
				console.error("Error fetching recovery codes", error);
			}
		},
		async disableTwoFactorAuth() {
			try {
				await axios.delete(`user/two-factor-authentication/${this.authStore.user.id}`);
				this.twoFaStep = 6;
				this.authStore.user.two_factor_enabled = false;
			} catch (error) {
				console.error("Error disabling 2FA", error);
			}
		},
		handleTwoFaInput(event, index) {
			const value = event.target.value;
			if (value.length > 1) {
				this.twoFaCode[index] = value[1];
			}
			if (value.length === 0) {
				this.twoFaCode[index] = "";
			}
			if (index < 5 && value.length === 1) {
				this.$refs.twoFaInputBoxes[index + 1].focus();
			}
		},
		handleTwoFaPaste(event) {
			event.preventDefault();
			const paste = event.clipboardData.getData("text");
			if (paste.length === 6 && /^\d+$/.test(paste)) {
				this.twoFaCode = paste.split("");
				this.$refs.twoFaInputBoxes[0].blur();
			}
		},
		downloadRecoveryCodes() {
			const blob = new Blob([this.recoveryCodes.join('\n')], { type: 'text/plain' });
			const url = URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.href = url;
			a.download = 'sums-2fa-recovery-codes.txt';
			a.click();
			URL.revokeObjectURL(url);
		},
		async verifyExistingTwoFactor() {
			try {
				const code = this.verifyTwoFaCode.join('');
				const response = await axios.post("user/verify-two-factor-code", {
					code: code,
					confirm: false,
				});
				if (response.status === 200) {
					// Verification successful, proceed to show QR code
					this.twoFaStep = 2;
					this.startTwoFactorAuth();
				}
			} catch (error) {
				if (error.response?.data?.message) {
					this.verifyTwoFaCodeErrors = error.response.data.message;
				} else {
					this.verifyTwoFaCodeErrors = "Invalid code. Please try again.";
				}
				console.error("Error verifying 2FA code", error);
			}
		},
		handleVerifyTwoFaInput(event, index) {
			const value = event.target.value;
			if (value.length > 1) {
				this.verifyTwoFaCode[index] = value[1];
			}
			if (value.length === 0) {
				this.verifyTwoFaCode[index] = "";
			}
			if (index < 5 && value.length === 1) {
				this.$refs.verifyTwoFaInputBoxes[index + 1].focus();
			}
		},
		handleVerifyTwoFaPaste(event) {
			event.preventDefault();
			const paste = event.clipboardData.getData("text");
			if (paste.length === 6 && /^\d+$/.test(paste)) {
				this.verifyTwoFaCode = paste.split("");
				this.$refs.verifyTwoFaInputBoxes[0].blur();
			}
		},
		async completeTwoFactorSetup() {
			// Refresh user data to update must_setup_two_factor
			await this.authStore.fetchUser();
			this.twoFaStep = 1;
			// Emit event to notify parent that setup is complete
			this.$emit('on-confirm');
		}
	},
}
</script>
